import './friendsPage.css';
import React, {Component} from "react";
import UserDetailsService from "../services/userDetailsService";
import {Navigate} from "react-router-dom";
import RedirectionService from "../services/redirectionService";
import {UserDto, BasicUserDto, FriendRequestResponseDto} from "../model/functionModels";
import {ToastErrorLevel} from "../../App";
import {withRouter} from "./withRouter";
import UserDisplay from "./userDisplay";
import FriendService from "../services/friendService";
import LoadingSpinner from "./loadingSpinner";
import i18nService from "../../i18n/i18nService";

interface FriendsState {
    navigation?: string,
    loggedInUser?: UserDto,
    allUsers?: (UserDto | BasicUserDto)[],
    pendingFriendRequests?: FriendRequestResponseDto[],
    newFriendUsername: string,
    inviteEmail?: string,
    friendError?: string,
    inviteError?: string,
    isLoading: boolean,
}

interface FriendsProps {
    toastGenerator: (body: string, level: ToastErrorLevel) => any,
    onLogout: any,
}

class FriendsPage extends Component<FriendsProps, FriendsState> {
    private unsubscribe?: () => void;

    constructor(props: FriendsProps) {
        super(props, {})
        this.state = {
            newFriendUsername: "",
            inviteEmail: "",
            isLoading: false,
        };
        this.handleFailure = this.handleFailure.bind(this);
        this.handleRedirection = this.handleRedirection.bind(this);
        this.refreshAll = this.refreshAll.bind(this);
        this.handleAddFriend = this.handleAddFriend.bind(this);
        this.handleInviteFriend = this.handleInviteFriend.bind(this);
        this.handleAcceptFriendRequest = this.handleAcceptFriendRequest.bind(this);
        this.handleRejectFriendRequest = this.handleRejectFriendRequest.bind(this);
        this.handleRemoveFriend = this.handleRemoveFriend.bind(this);
        this.handleFriendLinkClick = this.handleFriendLinkClick.bind(this);
    }

    async componentDidMount() {
        // Subscribe to locale changes to trigger re-render
        this.unsubscribe = i18nService.subscribe(() => {
            this.forceUpdate();
        });
        
        if (!this.state.navigation) {
            await this.refreshAll();
        }
    }

    componentWillUnmount() {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
    }

    async refreshAll() {
        // Parallelize API calls for better performance
        const promises = [
            UserDetailsService.getOwnUserDetails(async userDto => {
                this.setState({
                    loggedInUser: userDto,
                });
            }, async () => {
                // @ts-ignore
                const currentPath = this.props.location?.pathname || '/friends';
                this.setState({
                    navigation: RedirectionService.redirectToLogin(currentPath),
                })
            },
            this.handleRedirection),
            UserDetailsService.getAllUsersList(async users => {
                this.setState({
                    allUsers: users,
                })
            }, this.handleFailure, this.handleRedirection),
            FriendService.getPendingFriendRequests(async requests => {
                this.setState({
                    pendingFriendRequests: requests,
                });
            }, this.handleFailure, this.handleRedirection)
        ];
        
        // Wait for all API calls to complete in parallel
        await Promise.all(promises);
    }

    async handleRedirection(redirectionUrl: string) {
        if (redirectionUrl.startsWith("/login")) {
            this.props.onLogout();
        }
        this.setState({
            navigation: redirectionUrl
        });
    }

    async handleFailure(error: string) {
        this.props.toastGenerator(error, ToastErrorLevel.ERROR);
    }

    async handleAcceptFriendRequest(requestId: string) {
        this.setState({isLoading: true});
        await FriendService.acceptFriendRequest(
            {requestId},
            async () => {
                await this.refreshAll();
                this.props.toastGenerator(i18nService.t('friends.requestAccepted'), ToastErrorLevel.INFO);
            },
            this.handleFailure,
            this.handleRedirection
        );
        this.setState({isLoading: false});
    }

    async handleRejectFriendRequest(requestId: string) {
        this.setState({isLoading: true});
        await FriendService.rejectFriendRequest(
            {requestId},
            async () => {
                await this.refreshAll();
                this.props.toastGenerator(i18nService.t('friends.requestRejected'), ToastErrorLevel.INFO);
            },
            this.handleFailure,
            this.handleRedirection
        );
        this.setState({isLoading: false});
    }

    async handleAddFriend() {
        if (!this.state.newFriendUsername.trim()) {
            this.setState({friendError: i18nService.t('errors.usernameRequired')});
            return;
        }
        this.setState({isLoading: true, friendError: undefined});
        await FriendService.addFriend({
            username: this.state.newFriendUsername.trim(),
        }, async () => {
            this.props.toastGenerator(i18nService.t('friends.friendAddedSuccess'), ToastErrorLevel.INFO);
            this.setState({newFriendUsername: "", friendError: undefined});
            await this.refreshAll();
        }, async (error: string) => {
            this.setState({friendError: error});
            this.props.toastGenerator(error, ToastErrorLevel.ERROR);
        }, this.handleRedirection);
        this.setState({isLoading: false});
    }

    async handleInviteFriend() {
        const email = (this.state.inviteEmail || "").trim();
        if (!email || !email.includes("@")) {
            this.setState({inviteError: i18nService.t('errors.emailInvalid')});
            return;
        }
        this.setState({isLoading: true, inviteError: undefined});
        await FriendService.inviteFriend(
            {email: email},
            async () => {
                this.props.toastGenerator(i18nService.t('friends.invitationSent'), ToastErrorLevel.INFO);
                this.setState({inviteEmail: ""});
            },
            async (error: string) => {
                this.setState({inviteError: error});
                this.props.toastGenerator(error, ToastErrorLevel.ERROR);
            },
            this.handleRedirection
        );
        this.setState({isLoading: false});
    }

    handleFriendLinkClick(username: string) {
        const url = `${window.location.origin}/profile/add-friend/${encodeURIComponent(username)}`;
        navigator.clipboard.writeText(url).then(() => {
            this.props.toastGenerator(i18nService.t('friends.linkCopied'), ToastErrorLevel.INFO);
        }).catch(() => {
            // Fallback: select the text
            const textArea = document.createElement("textarea");
            textArea.value = url;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            this.props.toastGenerator(i18nService.t('friends.linkCopied'), ToastErrorLevel.INFO);
        });
    }

    getFriendDtos(): (UserDto | BasicUserDto)[] {
        if (!this.state.loggedInUser || !this.state.allUsers) {
            return [];
        }
        return this.state.allUsers.filter(u => u.id !== this.state.loggedInUser!.id && this.state.loggedInUser!.friends.includes(u.id));
    }

    async handleRemoveFriend(friendId: string) {
        if (!window.confirm(i18nService.t('friends.removeConfirm'))) {
            return;
        }
        this.setState({isLoading: true});
        await FriendService.removeFriend(
            friendId,
            async () => {
                this.props.toastGenerator(i18nService.t('friends.friendRemoved'), ToastErrorLevel.INFO);
                await this.refreshAll();
            },
            this.handleFailure,
            this.handleRedirection
        );
        this.setState({isLoading: false});
    }

    render() {
        if (this.state.navigation) {
            return <Navigate to={this.state.navigation}/>
        }
        if (!this.state.loggedInUser || !this.state.allUsers) {
            return <div className="FriendsPage-Loading">
                <LoadingSpinner size="large" message={i18nService.t('common.loading')} />
            </div>
        }
        const friends = this.getFriendDtos();
        const friendLink = `${window.location.origin}/profile/add-friend/${encodeURIComponent(this.state.loggedInUser.username)}`;

        return <div className="FriendsPage-Wrapper">
            <div className="FriendsPage-Container">
                <h1 className="FriendsPage-Title">{i18nService.t('friends.title')}</h1>
                
                <div className="FriendsPage-Section">
                    <h2 className="FriendsPage-SectionTitle">{i18nService.t('friends.pendingRequests')} ({this.state.pendingFriendRequests?.length || 0})</h2>
                    {this.state.pendingFriendRequests && this.state.pendingFriendRequests.length > 0 ? (
                        <div className="FriendsPage-FriendRequestsList">
                            {this.state.pendingFriendRequests.map(request => (
                                <div key={request.id} className="FriendsPage-FriendRequestItem">
                                    <UserDisplay user={request.fromUser} showName={true} abbreviateName={false} hideBackground={false} />
                                    <div className="FriendsPage-FriendRequestActions">
                                        <button
                                            className="FriendsPage-Button FriendsPage-ButtonPrimary"
                                            onClick={() => this.handleAcceptFriendRequest(request.id)}
                                            disabled={this.state.isLoading}
                                        >
                                            {i18nService.t('profile.accept')}
                                        </button>
                                        <button
                                            className="FriendsPage-Button FriendsPage-ButtonSecondary"
                                            onClick={() => this.handleRejectFriendRequest(request.id)}
                                            disabled={this.state.isLoading}
                                        >
                                            {i18nService.t('profile.reject')}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="FriendsPage-EmptyState">{i18nService.t('profile.noPendingRequests')}</p>
                    )}
                </div>

                <div className="FriendsPage-Section">
                    <h2 className="FriendsPage-SectionTitle">{i18nService.t('profile.friends')} ({friends.length})</h2>
                    {friends.length === 0 ? (
                        <p className="FriendsPage-EmptyState">{i18nService.t('friends.noFriends')}</p>
                    ) : (
                        <div className="FriendsPage-FriendsList">
                            {friends.map(f => (
                                <div key={`friend-${f.id}`} className="FriendsPage-FriendCard">
                                    <UserDisplay user={f} showName={true} abbreviateName={false} hideBackground={false}/>
                                    <button
                                        className="FriendsPage-FriendRemoveBtn"
                                        onClick={() => this.handleRemoveFriend(f.id)}
                                        disabled={this.state.isLoading}
                                        title={i18nService.t('friends.removeFriend')}
                                        aria-label={i18nService.t('friends.removeFriend')}
                                    >
                                        🗑️
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="FriendsPage-Section">
                    <h2 className="FriendsPage-SectionTitle">{i18nService.t('profile.addFriend')}</h2>
                    <p className="FriendsPage-SectionDescription">{i18nService.t('profile.addFriendDescription')}</p>
                    <div className="FriendsPage-InputGroup">
                        <input 
                            type="text" 
                            className="FriendsPage-Input"
                            placeholder={i18nService.t('profile.enterFriendUsername')} 
                            value={this.state.newFriendUsername} 
                            onChange={e => this.setState({newFriendUsername: e.target.value, friendError: undefined})}
                            disabled={this.state.isLoading}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    this.handleAddFriend();
                                }
                            }}
                        />
                        <button 
                            className="FriendsPage-Button"
                            onClick={this.handleAddFriend}
                            disabled={this.state.isLoading || !this.state.newFriendUsername.trim()}
                        >
                            {this.state.isLoading ? i18nService.t('friends.addingFriend') : i18nService.t('profile.addFriend')}
                        </button>
                    </div>
                    {this.state.friendError && (
                        <div className="FriendsPage-Error">{this.state.friendError}</div>
                    )}
                </div>

                <div className="FriendsPage-Section">
                    <h2 className="FriendsPage-SectionTitle">{i18nService.t('profile.inviteFriend')}</h2>
                    <p className="FriendsPage-SectionDescription">{i18nService.t('profile.inviteDescription')}</p>
                    <div className="FriendsPage-InputGroup">
                        <input
                            type="email"
                            className="FriendsPage-Input"
                            placeholder="friend@example.com"
                            value={this.state.inviteEmail}
                            onChange={(e) => this.setState({inviteEmail: e.target.value, inviteError: undefined})}
                            disabled={this.state.isLoading}
                        />
                        <button
                            className="FriendsPage-Button"
                            onClick={this.handleInviteFriend}
                            disabled={this.state.isLoading || !(this.state.inviteEmail || "").trim()}
                        >
                            {this.state.isLoading ? i18nService.t('friends.sending') : i18nService.t('profile.sendInvite')}
                        </button>
                    </div>
                    {this.state.inviteError && (
                        <div className="FriendsPage-Error">{this.state.inviteError}</div>
                    )}
                </div>

                <div className="FriendsPage-Section">
                    <h2 className="FriendsPage-SectionTitle">{i18nService.t('profile.friendLink')}</h2>
                    <p className="FriendsPage-SectionDescription">{i18nService.t('profile.friendLinkDescription')}</p>
                    <div className="FriendsPage-FriendLinkContainer">
                        <input
                            type="text"
                            className="FriendsPage-Input"
                            value={friendLink}
                            readOnly
                        />
                        <button
                            className="FriendsPage-Button"
                            onClick={() => this.handleFriendLinkClick(this.state.loggedInUser!.username)}
                            disabled={this.state.isLoading}
                        >
                            {i18nService.t('profile.copyLink')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    }
}

export default withRouter(FriendsPage);

